import { useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, Gavel, FileSearch, Scale, Handshake, Users, Milestone,
  ArrowRight, ListChecks, MessageCircleQuestion, AlertTriangle, Landmark,
} from "lucide-react";
import { ROADMAP_STAGES } from "@/data/legalData";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const stageIcons = { arrest: ShieldCheck, arraignment: Gavel, discovery: FileSearch, preliminary: Scale, plea: Handshake, trial: Users, sentencing: Milestone };

export default function Roadmap() {
  const [active, setActive] = useState(ROADMAP_STAGES[0].id);
  const stage = ROADMAP_STAGES.find((s) => s.id === active);
  const Icon = stageIcons[stage.id] || Milestone;
  const progress = (stage.n / ROADMAP_STAGES.length) * 100;

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Interactive case roadmap</span>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-semibold text-forest-900">The path through your case</h1>
          <p className="mt-4 text-ink-700 leading-relaxed">
            Every criminal case moves through stages. Select any stage to see what the prosecution is doing,
            what your defense should focus on, and the questions to bring to your attorney.
          </p>
        </div>

        {/* Progress rail */}
        <div className="mt-10 rounded-2xl border border-parchment-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-xs uppercase tracking-wider text-forest-600">Stage {stage.n} of {ROADMAP_STAGES.length}</span>
            <span className="font-mono text-xs text-ink-400">{Math.round(progress)}% through the process</span>
          </div>
          <div className="h-2 rounded-full bg-parchment-200 overflow-hidden">
            <motion.div className="h-full bg-forest-700 rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.5, ease: "easeInOut" }} />
          </div>
          <div className="mt-5 flex gap-2 overflow-x-auto thin-scroll pb-1">
            {ROADMAP_STAGES.map((s) => {
              const SI = stageIcons[s.id] || Milestone;
              const isActive = s.id === active;
              return (
                <button key={s.id} data-testid={`roadmap-stage-${s.id}-button`} onClick={() => setActive(s.id)}
                  className={`shrink-0 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border transition-all ${
                    isActive ? "bg-forest-800 text-parchment-50 border-forest-800"
                    : s.n < stage.n ? "bg-sage-500 text-white border-sage-500"
                    : "bg-white text-ink-500 border-parchment-200 hover:border-forest-600/40"}`}>
                  <SI className="h-4 w-4" /> {s.title}
                </button>
              );
            })}
          </div>
        </div>

        {/* Stage detail */}
        <motion.div key={stage.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="mt-8 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-forest-900 text-parchment-50 p-8 h-full">
              <span className="grid place-items-center h-14 w-14 rounded-2xl bg-parchment-50/10"><Icon className="h-7 w-7" /></span>
              <span className="mt-6 block font-mono text-xs uppercase tracking-wider text-sage-500">Stage {stage.n}</span>
              <h2 className="mt-1 font-serif text-3xl font-semibold">{stage.title}</h2>
              <p className="mt-4 text-parchment-200/80 leading-relaxed">{stage.summary}</p>
              <Link to="/assistant">
                <Button className="mt-8 rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">
                  Ask the AI about this stage <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-5">
            <Panel icon={Landmark} tone="ink" title="What the prosecution is doing" items={stage.prosecution} />
            <Panel icon={ShieldCheck} tone="forest" title="What your defense should focus on" items={stage.defense} />
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="rounded-2xl border border-parchment-200 bg-white p-6">
                <div className="flex items-center gap-2 text-forest-800 mb-3">
                  <MessageCircleQuestion className="h-5 w-5" />
                  <h3 className="font-semibold">Ask your lawyer</h3>
                </div>
                <ul className="space-y-2">
                  {stage.questions.map((q, i) => (
                    <li key={i} className="text-sm text-ink-700 flex gap-2"><span className="text-terracotta">•</span>{q}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border-l-4 border-terracotta bg-terracotta-subtle p-6">
                <div className="flex items-center gap-2 text-[#8C3415] mb-3">
                  <AlertTriangle className="h-5 w-5" />
                  <h3 className="font-semibold">Common pitfalls</h3>
                </div>
                <ul className="space-y-2">
                  {stage.risks.map((r, i) => (
                    <li key={i} className="text-sm text-[#8C3415] flex gap-2"><span>•</span>{r}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

const Panel = ({ icon: Icon, title, items, tone }) => (
  <div className="rounded-2xl border border-parchment-200 bg-white p-6">
    <div className={`flex items-center gap-2 mb-3 ${tone === "forest" ? "text-forest-800" : "text-ink-700"}`}>
      <Icon className="h-5 w-5" />
      <h3 className="font-semibold">{title}</h3>
    </div>
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="text-sm text-ink-700 flex gap-3">
          <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${tone === "forest" ? "bg-forest-600" : "bg-ink-400"}`} />
          {it}
        </li>
      ))}
    </ul>
  </div>
);
